import { useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { NextFilterAndSortListings } from "@/pages/root/shop/NextFilterAndSortListings";
import { convertProductTypeToListingType } from "@/utils/productTypes";
import { get } from "api";
import { AffirmBanner } from "atoms/affirm/affirmBanner/AffirmBanner";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import useDunningMembership from "hooks/useDunningMemberships";
import { AffirmModal } from "molecules/affirm-modal/AffirmModal";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { queryClient } from "pages/Root";
import { FilterAndSortListings } from "pages/root/shop/FilterAndSortListings";
import { useFeatureFlag } from "services/Flags";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";
import { notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const load = async () => {
  const shopData = queryClient.fetchQuery(["/shop"], () => get("/shop"));
  const listings = queryClient.fetchQuery(
    ["/listings"],
    () => get("/listings"),
    { staleTime: 1000 * 60 }
  );
  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );
  const categories = queryClient.fetchQuery(["shop/categories"], () =>
    get("/shop/categories")
  );
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });
  const recommendationIds = queryClient.fetchQuery(
    ["/adonis/top-recommendations/id"],
    () => get("/adonis/top-recommendations/id", { limit: 10 }),
    { staleTime: 1000 * 30 }
  );

  return waitObj({
    platform,
    shopData,
    listings,
    categories,
    theme,
    shopDiscounts,
    customShopCategories,
    user,
    recommendationIds,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const shopPackagesLoader: LoaderFunction = () => {
  return load();
};

const ShopPackagesPage = () => {
  const {
    platform,
    listings,
    shopData,
    categories,
    theme,
    shopDiscounts,
    customShopCategories,
    user,
    recommendationIds,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [showAffirmModal, setShowAffirmModal] = useState(false);
  const headerImage = theme.resources?.find(
    (resource) => resource.key === "curated-packages-header"
  )?.url;

  let packages = listings.filter((listing) => listing.type === "Package");
  packages = getListingsWithDiscountInformation(
    packages,
    shopDiscounts?.listingDiscounts,
    customShopCategories
  );

  const recommendedIds = new Set(recommendationIds);

  const filteredConcerns =
    shopData.concerns?.filter((concern) =>
      packages.flatMap((listing) => listing.concernIds).includes(concern.id)
    ) ?? [];

  const filteredAreas =
    shopData.areas?.filter((area) =>
      packages.flatMap((listing) => listing.areaIds).includes(area.id)
    ) ?? [];

  const packagesCategory = categories.find(
    (c) => c.defaultCategoryId === "packages"
  );
  const { dunningMembership } = useDunningMembership();
  const isAlgoliaFilteringEnabled = useFeatureFlag("algoliaFiltering");
  return (
    <div className="flex flex-col bg-two">
      {platform.platformSettings?.affirmEnabled && (
        <AffirmModal
          platformLogo={platform.logoImageUrl ?? ""}
          open={showAffirmModal}
          onClose={() => setShowAffirmModal(false)}
          hideRewardText={!platform?.platformSettings?.rewardsEnabled}
        />
      )}
      {packagesCategory && (
        <ShopListHeader
          title={packagesCategory.title}
          subtitle={packagesCategory.description}
          bgGradient
          // TODO(CAA-158) using the header image from theme until we implement CAA-158
          backgroundImageUrl={headerImage}
        />
      )}

      {!!packages.length && (
        <>
          {isAlgoliaFilteringEnabled ? (
            <NextFilterAndSortListings
              types={["Package"]}
              areas={shopData.areas ?? []}
              concerns={shopData.concerns ?? []}
              platformId={platform.id}
            >
              {(hit, sendEvent, index) => {
                const [enhancedHit] = getListingsWithDiscountInformation(
                  [{ ...hit, id: hit.objectID }],
                  shopDiscounts?.listingDiscounts,
                  customShopCategories
                );

                const handleClick = () => {
                  sendEvent("click", hit, "Hit Clicked");
                  navigate(`/pdp/package/${enhancedHit.objectID}`);
                };

                return (
                  <>
                    <ProductCardLarge
                      key={enhancedHit.objectID}
                      itemId={enhancedHit.objectID}
                      title={enhancedHit.title}
                      description={enhancedHit.description}
                      imageUrl={enhancedHit.imageUrl}
                      type={convertProductTypeToListingType(
                        enhancedHit.productType
                      )}
                      tag=""
                      concerns={enhancedHit.concernNames}
                      price={enhancedHit.minBasePrice}
                      memberPrice={null}
                      onClick={handleClick}
                      discount={enhancedHit.discountText}
                      customColor={enhancedHit.customColor}
                      guestMode={user.isGuest}
                      isRecommended={recommendedIds.has(enhancedHit.objectID)}
                    />
                    {dunningMembership.status === false &&
                      platform.platformSettings?.affirmEnabled &&
                      (packages.length < 2 || index === 1) && (
                        <AffirmBanner
                          onClick={() => setShowAffirmModal(true)}
                          hasColorGradient
                          hasBorderRadius
                          hasShadow
                          hideRewardText={
                            !platform?.platformSettings?.rewardsEnabled
                          }
                        />
                      )}
                  </>
                );
              }}
            </NextFilterAndSortListings>
          ) : (
            <FilterAndSortListings
              types={["Package"]}
              areas={filteredAreas}
              concerns={filteredConcerns}
              listings={packages}
            >
              {(listing, index) => (
                <>
                  <ProductCardLarge
                    key={listing.id}
                    itemId={listing.id}
                    title={listing.title}
                    description={listing.description}
                    imageUrl={listing.imageUrl}
                    type={listing.type}
                    tag=""
                    concerns={filteredConcerns
                      .filter(({ id }) => listing.concernIds.includes(id))
                      .map(({ name }) => name)
                      .filter(notNull)}
                    price={listing.baseCost}
                    memberPrice={listing.membershipCost}
                    onClick={() => navigate(`/pdp/package/${listing.id}`)}
                    discount={listing.discountText}
                    customColor={listing.customColor}
                    guestMode={user.isGuest}
                    isRecommended={recommendedIds.has(listing.id)}
                  />
                  {dunningMembership.status === false &&
                    platform.platformSettings?.affirmEnabled &&
                    (packages.length < 2 || index === 1) && (
                      <AffirmBanner
                        onClick={() => setShowAffirmModal(true)}
                        hasColorGradient
                        hasBorderRadius
                        hasShadow
                        hideRewardText={
                          !platform?.platformSettings?.rewardsEnabled
                        }
                      />
                    )}
                </>
              )}
            </FilterAndSortListings>
          )}
        </>
      )}
      <RepeatMDFooter />
    </div>
  );
};

export default ShopPackagesPage;
